/**
 * Document-level print styles for the embedded (Shadow DOM) widget.
 *
 * When the app runs standalone, `styles.css` is imported into the main
 * document, so its `@media print` rules — including `@page` and any host-chrome
 * hiding — apply to the whole page. When the app is embedded via the
 * `market-data-center` custom element, all widget styles live inside a Shadow
 * DOM (see `embed-entrypoint.tsx` and `asset-loading.ts`). Two consequences
 * break printing in that mode:
 *
 *   1. `@page` at-rules are ignored when they originate from a shadow root's
 *      stylesheet; they only take effect from the top-level document.
 *   2. Selectors scoped to the shadow root cannot reach the host page's chrome
 *      (Drupal header/nav/footer/sidebars), so a browser print includes all of
 *      the host layout around the widget.
 *
 * This module injects a small stylesheet into the *host document* to cover what
 * the shadow-scoped `print.css` cannot: page margins, hiding everything in the
 * host document except the widget, and promoting the widget to fill the page.
 * It intentionally avoids host-specific (Drupal) selectors so it keeps working
 * if the host theme changes: it hides every `<body>` child that is not on the
 * ancestor chain of a `market-data-center` element.
 */

const WIDGET_TAG = 'market-data-center';

/**
 * Host-document print CSS.
 *
 * `[data-mdc-print-host]` is set on the ancestor chain of each mounted widget
 * at print time (see `markPrintAncestors`). Everything else under `<body>` is
 * hidden for print; ancestors are reset to a plain, full-width block so the
 * widget is the only thing on the page.
 */
const PRINT_HOST_CSS = `
@media print {
  @page {
    margin: 0.5in;
  }

  html,
  body {
    margin: 0 !important;
    padding: 0 !important;
    background: #fff !important;
  }

  /* Hide every host-page element that is not an ancestor of the widget. The
     widget's own content lives in a Shadow DOM, so it has no light-DOM
     descendants for this rule to hide — only the host chrome and the widget's
     ancestor chain exist in the light DOM. Marked ancestors are exempt. */
  body *:not([data-mdc-print-host]) {
    display: none !important;
  }

  /* The widget and its ancestors render as a plain, full-width block. */
  [data-mdc-print-host] {
    display: block !important;
    position: static !important;
    float: none !important;
    width: auto !important;
    max-width: none !important;
    min-width: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
    border: 0 !important;
    background: transparent !important;
    box-shadow: none !important;
    overflow: visible !important;
  }

  ${WIDGET_TAG} {
    display: block !important;
    width: 100% !important;
  }
}
`;

let sheet: CSSStyleSheet | null = null;
let styleElement: HTMLStyleElement | null = null;

/**
 * Reference count of connected embeds. The host stylesheet is added when the
 * first embed connects and removed when the last one disconnects.
 */
let refCount = 0;

/**
 * Whether the current environment supports constructable stylesheets on the
 * document. jsdom (tests) and older browsers do not, so we fall back to a
 * `<style>` element.
 */
function supportsAdoptedStyleSheets(doc: Document): boolean {
  return (
    typeof CSSStyleSheet !== 'undefined' &&
    'replaceSync' in CSSStyleSheet.prototype &&
    Array.isArray(doc.adoptedStyleSheets)
  );
}

function addHostStyles(doc: Document) {
  if (supportsAdoptedStyleSheets(doc)) {
    if (!sheet) {
      sheet = new CSSStyleSheet();
      sheet.replaceSync(PRINT_HOST_CSS);
    }
    if (!doc.adoptedStyleSheets.includes(sheet)) {
      doc.adoptedStyleSheets = [...doc.adoptedStyleSheets, sheet];
    }
    return;
  }

  // Fallback: inject a <style> element.
  if (!styleElement || !styleElement.isConnected) {
    styleElement = doc.createElement('style');
    styleElement.setAttribute('data-mdc-print-host-styles', '');
    styleElement.media = 'print';
    styleElement.textContent = PRINT_HOST_CSS;
    doc.head.appendChild(styleElement);
  }
}

function removeHostStyles(doc: Document) {
  if (sheet && doc.adoptedStyleSheets.includes(sheet)) {
    doc.adoptedStyleSheets = doc.adoptedStyleSheets.filter((s) => s !== sheet);
  }
  if (styleElement?.isConnected) {
    styleElement.remove();
  }
  styleElement = null;
}

/**
 * Per-element count of how many mounted embeds require this element to stay
 * marked as a print ancestor. Shared ancestors (e.g., `<body>`) are marked by
 * multiple embeds; the attribute is only removed once the last embed that
 * needed it disconnects.
 */
const ancestorMarkCounts = new WeakMap<Element, number>();

/**
 * Marks the ancestor chain of `element` up to (and including) `<body>` with the
 * `data-mdc-print-host` attribute so the print CSS can keep them visible.
 *
 * The attribute only has an effect under `@media print`, so it is safe to leave
 * applied for the lifetime of the embed. Marking persistently (rather than only
 * on the `beforeprint` event) keeps printing working in browsers that do not
 * fire `beforeprint`/`afterprint` (e.g., Safari) and when the user prints via
 * the browser menu rather than an in-app button.
 *
 * Uses per-element reference counting so a shared ancestor (such as `<body>`)
 * stays marked until the last embed that needs it is cleaned up.
 *
 * Returns a cleanup function that decrements the counts this call incremented
 * and removes the attribute from elements whose count reaches zero.
 */
function markPrintAncestors(element: Element): () => void {
  const chain: Element[] = [];
  let node: Element | null = element;

  while (node && node !== node.ownerDocument.documentElement) {
    const next = (ancestorMarkCounts.get(node) ?? 0) + 1;
    ancestorMarkCounts.set(node, next);
    node.setAttribute('data-mdc-print-host', '');
    chain.push(node);
    node = node.parentElement;
  }

  return () => {
    for (const el of chain) {
      const remaining = (ancestorMarkCounts.get(el) ?? 1) - 1;
      if (remaining <= 0) {
        ancestorMarkCounts.delete(el);
        el.removeAttribute('data-mdc-print-host');
      } else {
        ancestorMarkCounts.set(el, remaining);
      }
    }
  };
}

/**
 * Registers a mounted embed's host element for document-level print support.
 *
 * @param host - The `market-data-center` custom element instance.
 * @returns A cleanup function to call when the embed disconnects.
 */
export function ensureHostPrintStyles(host: HTMLElement): () => void {
  const doc = host.ownerDocument;

  refCount += 1;
  addHostStyles(doc);

  // Mark the widget's ancestor chain so the print CSS can keep it visible.
  // Applied for the lifetime of the embed (see `markPrintAncestors`).
  const cleanupAncestors = markPrintAncestors(host);

  return () => {
    cleanupAncestors();

    refCount = Math.max(0, refCount - 1);
    if (refCount === 0) {
      removeHostStyles(doc);
    }
  };
}
